import { z } from "zod";

import { broadcastStateSchema } from "@/types/broadcast";

const nullableDateTimeSchema = z.string().nullable();

const sendDataSchema = z.object({
  subject: z.string(),
  from_name: z.string(),
  preheader: z.string(),
  from_email: z.string(),
  first_name: z.string(),
  reply_to: z.string(),
});

const paginatedEmailBroadcastSchema = z.object({
  type: z.literal("email"),
  id: z.string(),
  site: z.string(),
  name: z.string(),
  state: broadcastStateSchema,
  scheduled_date: nullableDateTimeSchema,
  send_start_date: nullableDateTimeSchema,
  send_complete_date: nullableDateTimeSchema,
  resend_scheduled_date: nullableDateTimeSchema,
  resend_start_date: nullableDateTimeSchema,
  resend_complete_date: nullableDateTimeSchema,
  send_data: sendDataSchema,
  email: z.string(), // ID of the email_creative linked to the frames email frame_variant
  create_date: z.string(),
  modify_date: z.string(),
  archived_date: nullableDateTimeSchema,
  is_transactional: z.boolean(),
  email_lists: z.array(z.string()),
  email_lists_details: z.array(z.unknown()),
  segments: z.array(z.string()),
  segments_details: z.array(z.unknown()),
  frames: z.array(z.string()),
});

const paginatedSmsBroadcastSchema = z.object({
  type: z.literal("sms"),
  id: z.string(),
  site: z.string(),
  name: z.string(),
  state: broadcastStateSchema,
  scheduled_date: nullableDateTimeSchema,
  create_date: z.string(),
  modify_date: z.string(),
  archived_date: nullableDateTimeSchema,
  queued_date: nullableDateTimeSchema,
  completed_date: nullableDateTimeSchema,
  cancelled_date: nullableDateTimeSchema,
  sent_date: nullableDateTimeSchema,
  email_lists: z.array(z.string()),
  segments: z.array(z.string()),
  frames: z.array(z.string()),
});

export const paginatedBroadcastSchema = z.discriminatedUnion("type", [
  paginatedEmailBroadcastSchema,
  paginatedSmsBroadcastSchema,
]);

export const paginatedBroadcastsResponseSchema = z.object({
  count: z.number().int().min(0),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(paginatedBroadcastSchema),
});

export const paginatedBroadcastsParamsSchema = z.object({
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  states: z.array(broadcastStateSchema).optional(),
  types: z.array(z.enum(["email", "sms"])).optional(),
  search: z.string().optional(),
});

export type PaginatedBroadcast = z.infer<typeof paginatedBroadcastSchema>;
export type PaginatedBroadcastsResponse = z.infer<
  typeof paginatedBroadcastsResponseSchema
>;
export type PaginatedBroadcastsParams = z.infer<
  typeof paginatedBroadcastsParamsSchema
>;

// --- Audiences broadcasts: shared types ---

export const mediumTypeSchema = z.enum([
  "MEDIUM_TYPE_EMAIL",
  "MEDIUM_TYPE_SMS",
  "MEDIUM_TYPE_MMS",
  "MEDIUM_TYPE_RCS",
  "MEDIUM_TYPE_ONSITE",
  "MEDIUM_TYPE_FRAMES",
]);

/** Audiences `Broadcast.force_channel` buf.validate allows enum numeric values 1 and 2 only (email + SMS). */
export const broadcastForceChannelSchema = z.enum([
  "MEDIUM_TYPE_EMAIL",
  "MEDIUM_TYPE_SMS",
]);

/** Audiences `Broadcast.email_options` (protobuf JSON camelCase). */
export const audiencesEmailOptionsSchema = z.object({
  fromName: z.string().optional(),
  fromEmail: z.string().optional(),
  replyTo: z.string().optional(),
});

export type AudiencesEmailOptions = z.infer<typeof audiencesEmailOptionsSchema>;

/** Max length for broadcast name in compose UI and trimmed API payloads. */
export const BROADCAST_NAME_MAX_LENGTH = 256;

/**
 * Default Smart Audience trim percentage. Shown on the slider when the toggle is
 * on without an explicit percentage (e.g. a legacy bool-only broadcast, whose
 * fixed behavior was 20%).
 */
export const DEFAULT_REACH_OPTIMIZATION_PERCENTAGE = 20;

/** Audiences POST `/{siteId}/broadcasts` response body. */
export const createBroadcastResponseSchema = z.object({
  id: z.string(),
});

export type MediumType = z.infer<typeof mediumTypeSchema>;
export type BroadcastForceChannel = z.infer<typeof broadcastForceChannelSchema>;
export type CreateBroadcastResponse = z.infer<
  typeof createBroadcastResponseSchema
>;

// --- Broadcast schedule (singular inline object) ---

export const broadcastScheduleSchema = z.union([
  z.object({ scheduledAt: z.string() }),
  z.object({ now: z.literal(true) }),
]);

export type BroadcastSchedule = z.infer<typeof broadcastScheduleSchema>;

// --- Variations + A/B test wire types ---

export const winningMetricWireSchema = z.enum([
  "AB_TEST_METRIC_OPEN_RATE",
  "AB_TEST_METRIC_CLICK_RATE",
  "AB_TEST_METRIC_ORDER_PLACED_RATE",
]);

export type WinningMetricWire = z.infer<typeof winningMetricWireSchema>;

/** A reported rate (a ratio — see RATE_SCALE in useBroadcastV2Metrics) with its raw count. */
export const rateCountSchema = z.object({
  rate: z.number(),
  count: z.string(),
});

/** A monetary value reported in both USD and the site's local currency. */
export const moneyValueSchema = z.object({
  usdValue: z.number(),
  localCurrencyValue: z.number(),
});

/**
 * Transitional money parser. The audiences backend is migrating `revenue` /
 * `average_order_value` from a bare number (local-currency double) to a
 * `MoneyValue` object; staging still emits numbers. Accept either and normalize
 * to `MoneyValue`. Remove the number branch once staging emits MoneyValue.
 */
export const tolerantMoneyValueSchema = z.union([
  moneyValueSchema,
  z.number().transform((n) => ({ usdValue: n, localCurrencyValue: n })),
]);

/**
 * Per-variation A/B results, embedded on the broadcast for done/sent broadcasts.
 * Counts are serialized as JSON strings; rates are ratios (see RATE_SCALE).
 */
export const broadcastVariationResultSchema = z.object({
  deliveries: z.string(),
  openRate: rateCountSchema,
  clickRate: rateCountSchema,
  placedOrderRate: rateCountSchema,
  revenue: tolerantMoneyValueSchema,
  averageOrderValue: tolerantMoneyValueSchema,
});

export type BroadcastVariationResultWire = z.infer<
  typeof broadcastVariationResultSchema
>;

export const broadcastVariationSchema = z.object({
  frameId: z.string(),
  emailOptions: audiencesEmailOptionsSchema.optional(),
  /** Send-time snapshot of the creative's subject/preheader (FOG-1871). */
  subjectLine: z.string().optional(),
  previewText: z.string().optional(),
  isWinner: z.boolean().optional(),
  /** Present only on done/sent broadcasts. */
  result: broadcastVariationResultSchema.optional(),
});

export type BroadcastVariation = z.infer<typeof broadcastVariationSchema>;

/** Wire-format A/B test option (int64 fields serialized as JSON strings). */
export const abTestOptionSchema = z.object({
  winningMetric: winningMetricWireSchema,
  testSizePercentage: z.string(),
  testDurationHours: z.string(),
});

export type AbTestOption = z.infer<typeof abTestOptionSchema>;

// --- CreateBroadcast request ---

// Smart Audience trim percentage on the wire: an int64-encoded string. Create
// requires 1–99; update and the persisted/read value also accept "0" (which
// clears the stored percentage). These guard the payload builders — which
// serialize an unrestricted number — so invalid int64 strings ("100", "1.5",
// "-1", "NaN") never reach the API. The response uses the 0–99 variant so a
// stray/legacy "0" degrades via hydration instead of throwing the whole load.
export const createReachOptimizationPercentageSchema = z
  .string()
  .regex(/^(?:[1-9]|[1-9]\d)$/);
export const persistedReachOptimizationPercentageSchema = z
  .string()
  .regex(/^(?:0|[1-9]|[1-9]\d)$/);

export const createBroadcastRequestV2Schema = z.object({
  name: z.string(),
  audienceIds: z.array(z.string()),
  /** Audiences whose members are suppressed from the send ("do not send to"). */
  excludedAudienceIds: z.array(z.string()).optional(),
  transactional: z.boolean().optional(),
  forceChannel: broadcastForceChannelSchema.optional(),
  schedule: broadcastScheduleSchema.optional(),
  variations: z.array(broadcastVariationSchema).min(1),
  abTestOption: abTestOptionSchema.optional(),
  smartSend: z.boolean().optional(),
  /** Smart Audience: trims the lowest-engagement recipients at send time. */
  reachOptimization: z.boolean().optional(),
  /**
   * Smart Audience trim percentage (1–99 on create), an int64-encoded string on
   * the wire (grpc-gateway). A positive percentage takes precedence over the
   * `reachOptimization` bool at send time.
   */
  reachOptimizationPercentage:
    createReachOptimizationPercentageSchema.optional(),
  enforceFrequencyCap: z.boolean().optional(),
  /** System that owns this broadcast ("marketing_calendar"); omit for hand-made. */
  source: z.string().optional(),
});

export type CreateBroadcastRequestV2 = z.infer<
  typeof createBroadcastRequestV2Schema
>;

export const audiencesCreateBroadcastRequestV2Schema = z.object({
  siteId: z.string(),
  broadcast: createBroadcastRequestV2Schema,
});

export type AudiencesCreateBroadcastRequestV2 = z.infer<
  typeof audiencesCreateBroadcastRequestV2Schema
>;

// --- Audiences ListBroadcasts / GetBroadcast / CloneBroadcast / UpdateBroadcastStatus ---

export const audiencesBroadcastStatusSchema = z.enum([
  "BROADCAST_STATUS_UNSPECIFIED",
  "BROADCAST_STATUS_DRAFT",
  "BROADCAST_STATUS_ACTIVE",
  "BROADCAST_STATUS_ARCHIVED",
  "BROADCAST_STATUS_IN_PROGRESS",
  "BROADCAST_STATUS_DONE",
  "BROADCAST_STATUS_PARTIAL_FAILED",
  "BROADCAST_STATUS_FAILED",
]);

export type AudiencesBroadcastStatus = z.infer<
  typeof audiencesBroadcastStatusSchema
>;

/**
 * Read-time status schema is forward-compatible so newly-added backend statuses
 * do not crash parsing before frontend can explicitly support them.
 */
export const audiencesBroadcastReadStatusSchema = z.union([
  audiencesBroadcastStatusSchema,
  z.string().regex(/^BROADCAST_STATUS_[A-Z0-9_]+$/),
]);

export type AudiencesBroadcastReadStatus = z.infer<
  typeof audiencesBroadcastReadStatusSchema
>;

export function isKnownAudiencesBroadcastStatus(
  status: string,
): status is AudiencesBroadcastStatus {
  return audiencesBroadcastStatusSchema.safeParse(status).success;
}

/**
 * True for broadcasts that have finished sending (fully, partially, or with a
 * terminal failure) — i.e. the broadcast has reached a completed/sent state and
 * should render the completed view rather than the editable compose form.
 */
export function isV2DoneStatus(status?: AudiencesBroadcastReadStatus): boolean {
  return (
    status === "BROADCAST_STATUS_DONE" ||
    status === "BROADCAST_STATUS_PARTIAL_FAILED" ||
    status === "BROADCAST_STATUS_FAILED"
  );
}

/**
 * True for broadcasts that should open their report (completed) view rather than
 * the editable compose form. This is every done/sent status plus `ARCHIVED`:
 * archiving overwrites the prior lifecycle status, so an archived broadcast may
 * have been sent or may have been a draft — but either way the compose form is
 * the wrong destination for something that's been shelved. Archived drafts simply
 * surface an empty report (the completed view already gates on its data loading).
 */
export function isV2ReportableStatus(
  status?: AudiencesBroadcastReadStatus,
): boolean {
  return isV2DoneStatus(status) || status === "BROADCAST_STATUS_ARCHIVED";
}

/**
 * Send-time audience counts snapshotted when the broadcast was sent. The counts
 * arrive as int64-encoded strings on the wire; `z.coerce.number()` is the single
 * decode point so downstream code works in numbers. Omitted for broadcasts sent
 * before the backend began persisting these.
 */
export const broadcastStatSchema = z.object({
  recipientCount: z.coerce.number(),
  preOptimizationCount: z.coerce.number(),
  excludedCount: z.coerce.number(),
});

export type BroadcastStat = z.infer<typeof broadcastStatSchema>;

export const audiencesBroadcastSchema = z.object({
  id: z.string(),
  name: z.string(),
  audienceIds: z.array(z.string()).optional(),
  audiences: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
  /**
   * Excluded ("do not send to") audience ids, returned separately from the
   * target `audienceIds`. The output-only `audiences` array carries names for
   * both target and excluded audiences.
   */
  excludedAudienceIds: z.array(z.string()).optional(),
  transactional: z.boolean().optional(),
  forceChannel: mediumTypeSchema.optional(),
  status: audiencesBroadcastReadStatusSchema.optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  schedule: broadcastScheduleSchema.nullable().optional(),
  variations: z.array(broadcastVariationSchema).optional(),
  abTestOption: abTestOptionSchema.optional(),
  smartSend: z.boolean().optional(),
  /** Smart Audience toggle (NOT `smartSend`). */
  reachOptimization: z.boolean().optional(),
  /** Smart Audience trim percentage, int64-encoded as a string on the wire. */
  reachOptimizationPercentage:
    persistedReachOptimizationPercentageSchema.optional(),
  /** True when the broadcast was sent instantly (vs. scheduled). */
  instantSend: z.boolean().optional(),
  broadcastStat: broadcastStatSchema.nullish(),
  enforceFrequencyCap: z.boolean().optional(),
  /** System that owns this broadcast ("marketing_calendar"); unset = hand-made. */
  source: z.string().nullish(),
});

export type AudiencesBroadcast = z.infer<typeof audiencesBroadcastSchema>;

export const audiencesPaginationResponseSchema = z.object({
  nextCursor: z.string().optional(),
  totalCount: z.string().optional(),
  total: z.string().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
});

export const listBroadcastsResponseSchema = z.object({
  broadcasts: z.array(audiencesBroadcastSchema).optional(),
  pagination: audiencesPaginationResponseSchema.optional(),
});

export type ListBroadcastsResponse = z.infer<
  typeof listBroadcastsResponseSchema
>;

export const cloneBroadcastResponseSchema = z.object({
  id: z.string(),
});

export type CloneBroadcastResponse = z.infer<
  typeof cloneBroadcastResponseSchema
>;

// --- GetBroadcast ---

export const getBroadcastResponseSchema = z.object({
  broadcast: audiencesBroadcastSchema,
});

export type GetBroadcastResponse = z.infer<typeof getBroadcastResponseSchema>;

// --- UpdateBroadcast (consolidated patch-style) ---

export const updateBroadcastRequestV2Schema = z.object({
  name: z.string().optional(),
  transactional: z.boolean().optional(),
  forceChannel: broadcastForceChannelSchema.optional(),
  abTestOption: abTestOptionSchema.optional(),
  updateVariations: z.array(broadcastVariationSchema).optional(),
  addVariations: z.array(broadcastVariationSchema).optional(),
  removeVariations: z.array(z.string()).optional(),
  schedule: broadcastScheduleSchema.optional(),
  addAudienceIds: z.array(z.string()).optional(),
  removeAudienceIds: z.array(z.string()).optional(),
  /** Differential exclusion edits, mirroring the target add/remove fields. */
  addExcludedAudienceIds: z.array(z.string()).optional(),
  removeExcludedAudienceIds: z.array(z.string()).optional(),
  smartSend: z.boolean().optional(),
  /**
   * Smart Audience toggle. `optional bool`: omit to leave unchanged, send
   * `false` to turn off. DRAFT-only (UpdateBroadcast rejects edits otherwise).
   */
  reachOptimization: z.boolean().optional(),
  /**
   * Smart Audience trim percentage, int64-encoded as a string on the wire.
   * Valid range on update is 0–99, where `"0"` clears the stored percentage
   * (turning it off, independent of the bool). Omit to leave unchanged.
   */
  reachOptimizationPercentage:
    persistedReachOptimizationPercentageSchema.optional(),
  enforceFrequencyCap: z.boolean().optional(),
  /** Owning-system stamp; an explicit "" clears it (unlink to hand-made). */
  source: z.string().optional(),
});

export type UpdateBroadcastRequestV2 = z.infer<
  typeof updateBroadcastRequestV2Schema
>;

export const updateBroadcastResponseSchema = z.object({
  success: z.boolean(),
});

export type UpdateBroadcastResponse = z.infer<
  typeof updateBroadcastResponseSchema
>;

// --- AddAudiences / RemoveAudiences ---

export const addAudiencesRequestSchema = z.object({
  audienceIds: z.array(z.string().uuid()).min(1),
});

export const removeAudiencesRequestSchema = z.object({
  audienceIds: z.array(z.string().uuid()).min(1),
});

export type AddAudiencesRequest = z.infer<typeof addAudiencesRequestSchema>;
export type RemoveAudiencesRequest = z.infer<
  typeof removeAudiencesRequestSchema
>;
